import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { style } from '../../../components/pdf/fontstyles';
import { AutoText } from '../../../components/pdf/autoText';

// Stylesheet
const styles = StyleSheet.create({
  table: {
    marginTop: 10,
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
  },
  row: {
    flexDirection: 'row',
    borderBottom: '1px solid #000',
  },
  cell: {
    padding: 5,
    borderRight: '1px solid #000',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: '#f2f2f2',
  },
  table1: { width: '25%' },
  table2: { width: '16.67%' },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    marginTop: 20
  },
  subSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    marginTop: 5
  },
  calculationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  indentedRow: {
    marginLeft: 15,
    marginBottom: 4,
  },
  calculationLabel: {
    marginTop: 2,
    fontSize: 10,
    flex: 2,
  },
  calculationValue: {
    fontSize: 10,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'right',
  },
  calculationNote: {
    fontSize: 9,
    marginLeft: 10,
  }
});

const RoughCalculation = ({ result }) => {
  // Ensure result has required properties
  if (!result || !result.dateOfRetirement || !result.payrateTable || !result.calculation || !result.calculation[0]) {
    console.error("Missing required data in RoughCalculation2 component");
    return (
      <View style={style.page}>
        <Text style={[style.title, style.ArialBlack]}>
          Error: Missing required data for calculation
        </Text>
      </View>
    );
  }

  // Safely access properties
  const payrateTable = result.payrateTable || [];
  const calculation = result.calculation[0] || {};
  const PensionablePayAvg = calculation.PensionablePayAvg || {};
  const basicPay = result.basicPay || 0;
  const pension = result.pension || 0;
  const commutedPension = result.commutedPension || 0;
  
  // Date calculations
  const [d, m, y] = result.dateOfRetirement.split('/').map(Number);
  const retirementDate = new Date(y, m - 1, d);
  
  const startEnhanced = new Date(retirementDate);
  startEnhanced.setDate(startEnhanced.getDate() + 1);
  
  const endEnhanced = new Date(retirementDate);
  endEnhanced.setFullYear(endEnhanced.getFullYear() + 10);
  
  const formatDate = (date) => 
    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

  // Formatted dates
  const enhancedStart = formatDate(startEnhanced);
  const enhancedEnd = formatDate(endEnhanced);
  const normalStart = formatDate(new Date(endEnhanced.setDate(endEnhanced.getDate() + 1)));

  // Table data
  const personalDetailsTable = [
    [result.dateOfBirth || '--', result.dateOfAppointment || '--', result.dateOfRetirement || '--', result.dateOfService || '--'],
  ];
  
  const pensionableServiceTable = [
    [
      calculation.pensionableService?.years || 0,
      calculation.pensionableService?.months || 0,
      calculation.pensionableService?.days || 0,
      calculation.terms || 0
    ],
  ];
  
  // Safe access to payrateTable elements
  const pensionablePayTable = [
    [
      payrateTable[0]?.from || '--',
      payrateTable[0]?.to || '--',
      `${PensionablePayAvg.period1?.months || 0} M ${(PensionablePayAvg.period1?.Dfirst || 0) + (PensionablePayAvg.period1?.Dsecond || 0)} D`,
      payrateTable[0]?.payrate || 0,
      PensionablePayAvg.total1 || 0,
      '-'
    ],
    [
      payrateTable[1]?.from || '--',
      payrateTable[1]?.to || '--',
      `${PensionablePayAvg.period2?.months || 0} M ${(PensionablePayAvg.period2?.Dfirst || 0) + (PensionablePayAvg.period1?.Dsecond || 0)} D`,
      payrateTable[1]?.payrate || 0,
      PensionablePayAvg.total2 || 0,
      '-'
    ],
    [
      payrateTable[0]?.from || '--',
      payrateTable[1]?.to || payrateTable[0]?.to || '--',
      '10 Months',
      '--',
      PensionablePayAvg.netTotal || 0,
      `Avg. ${Math.round((PensionablePayAvg.netTotal || 0) / 10)}`
    ],
  ];

  // Calculations
  const gratuityFactor = calculation.gratuityFactor || 0;
  const gratuityAmount = Math.round(basicPay * gratuityFactor);
  const cappedGratuity = Math.min(2000000, gratuityAmount);
  const residualPension = pension - commutedPension;
  const commutationFactor = calculation.commutationFactor || 0;
  const commutedValue = Math.round(commutedPension * 12 * commutationFactor);
  const enhancedFamilyPension = Math.round(basicPay * 0.5);
  const normalFamilyPension = Math.round(basicPay * 0.3);

  return (
    <View style={style.page}>
      {/* Header Section */}
      <Text style={[style.title, style.ArialBlack]}>
        Rough Calculation of Pension, Gratuity and Commutation Value
      </Text>
        <Text style={[styles.subtitle, style.ArialItalic, {textAlign:'center'}]}>
              (Continue..)
        </Text>
      <View style={style.solidLine}></View>


      {/* Commutation Calculation */}
        <Text style={[styles.paragraph, {marginTop:20}]}>
              5) Commutation of Pension:
        </Text>
      <View style={[styles.table]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, { flex: 2 }]}>Commuted pension and commuted value:</Text>
          <AutoText style={[styles.cell, { flex: 1 }]}>Amount (₹)</AutoText>
        </View>
        <View style={styles.row}>
          <Text style={[styles.cell, { flex: 2 }]}>
            Commuted Pension = 40% of Pension = {commutedPension}
          </Text>
          <Text style={[styles.cell, { flex: 1 }]}>{commutedPension}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[styles.cell, { flex: 2 }]}>
            Residual Pension = Pension - Commuted Pension = {pension} - {commutedPension}
          </Text>
          <Text style={[styles.cell, { flex: 1 }]}>{residualPension}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[styles.cell, { flex: 2 }]}>
            Commuted Value = {commutationFactor} × 12 × {commutedPension}
          </Text>
          <Text style={[styles.cell, { flex: 1 }]}>{commutedValue}</Text>
        </View>
      </View>
      
      {/* Family Pension Calculation */}
        <Text style={[styles.paragraph, {marginTop:10}]}>
              6) Calculation of Family Pension:
        </Text>
      <View style={[styles.table]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, { flex: 2 }]}>Family Pension</Text>
          <AutoText style={[styles.cell, { flex: 1 }]}>Amount (₹)</AutoText>
        </View>
        <View style={styles.row}>
          <AutoText style={[styles.cell, { flex: 2 }]}>
            Enhanced Rate (50% of ₹{basicPay}) — {enhancedStart} to {enhancedEnd}
          </AutoText>
          <Text style={[styles.cell, { flex: 1 }]}>{enhancedFamilyPension}</Text>
        </View>
        <View style={styles.row}>
          <AutoText style={[styles.cell, { flex: 2 }]}>
            Normal Rate (30% of ₹{basicPay}) — From {normalStart}
          </AutoText>
          <Text style={[styles.cell, { flex: 1 }]}>{normalFamilyPension}</Text>
        </View>
      </View>
    </View>

  );
};

export default RoughCalculation;